import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { D3BasicLinePopupComponent } from '../grid/d3-basic-line-popup/d3-basic-line-popup.component';

@Component({
    selector: 'app-patients-trend-icon',
    template: `
  <span style="display: inline-flex; align-items: center; gap: 6px; line-height: 1;">
    <span>{{ displayValue }}</span>
    <mat-icon
      *ngIf="matched?.icon"
      [svgIcon]="matched.icon"
      [style.color]="matched.color"
      style="width: 20px; height: 20px; font-size: 20px; overflow: visible; display: inline-flex; align-items: center; justify-content: center; cursor: pointer;"
      (click)="openTrendPopup($event)">
    </mat-icon>
  </span>
  `,
    standalone: false
})
export class PatientsTrendIconComponent implements ICellRendererAngularComp {
  params!: ICellRendererParams | any;
  matched: any;
  displayValue: any = '';

  constructor(private dialog: MatDialog) {}

  agInit(params: ICellRendererParams<any> | any): void {
    this.params = params;
    this.displayValue = this.formatValue(params);

    const trendIcons: any[] = params?.trendIcons || [];
    const trendVariable: string = params?.trendVariable;

    if (!trendIcons.length) {
      this.matched = null;
      return;
    }

    const trendValue = trendVariable && params?.data ? params.data[trendVariable] : undefined;

    const isEmpty = (v: any) => v === undefined || v === null || String(v).trim() === '';

    let match: any = null;
    if (!isEmpty(trendValue)) {
      match = trendIcons.find((t: any) =>
        !isEmpty(t?.trendName) &&
        String(t.trendName).toLowerCase() === String(trendValue).toLowerCase()
      );
    }

    if (!match) {
      match = trendIcons.find((t: any) => isEmpty(t?.trendName));
    }

    this.matched = match || null;
  }

  refresh(): boolean {
    return false;
  }

  openTrendPopup(event: MouseEvent): void {
    event.stopPropagation();

    const rowData = this.params?.data || {};

    // Map row fields → POST payload keys. Caller-supplied
    // `popupPayloadFields` (e.g. `{ source_key: 'vendor_key' }`) wins; the
    // default below is the common case for this grid — send the row's
    // `vendor_key` as `source_key`.
    const fieldMap: Record<string, string> =
      this.params?.popupPayloadFields || { source_key: 'vendor_key' };

    const payload: Record<string, any> = { ...(this.params?.popupExtraPayload || {}) };
    for (const [payloadKey, rowField] of Object.entries(fieldMap)) {
      const v = rowData[rowField];
      if (v !== undefined) payload[payloadKey] = v;
    }

    const heading =
      this.params?.popupHeading || rowData.vendor || 'Trend';

    this.dialog.open(D3BasicLinePopupComponent, {
      width: '70%',
      height: '70%',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'd3-basic-line-popup-dialog',
      data: {
        heading,
        apiKey: this.params?.popupApiKey || 20021111,
        endpoint: this.params?.popupEndpoint || 'PSS/get-pss-data',
        payload,
        props: this.params?.popupProps,
      },
    });
  }

  private formatValue(params: any): string {
    const raw = params?.valueFormatted ?? params?.value;
    if (raw === undefined || raw === null || raw === '') return '';

    if (params?.isFormats === 'Percentage') {
      if (raw === '-') return '-';
      const num = Number(raw);
      if (Number.isNaN(num)) return String(raw);
      if (num === 0) return '0%';
      const [whole, decimal] = String(raw).split('.');
      const formattedDecimal = decimal
        ? '.' + (decimal.length === 1 ? decimal + '0' : decimal.slice(0, 2))
        : '';
      return `${whole}${formattedDecimal}%`;
    }

    const prefix = params?.prefix ? params.prefix + ' ' : '';
    const suffix = params?.suffix ? ' ' + params.suffix : '';
    return `${prefix}${raw}${suffix}`.trim();
  }
}
